import { FiThumbsUp, FiThumbsDown } from "react-icons/fi";
import * as questionAidRatingsApi from "api/question_aid_ratings";
import { CSSTransition } from "react-transition-group";

import { useState, useEffect, useRef } from "react";
import { useUser } from "hooks/user";
import { isMobile } from "utils/browser";
import { useRouter } from "next/router";

import clsx from "clsx";
import ChatButton from "./ChatButton";
import ChatInterface from "./ChatInterface";

import { Question } from "types/question";
import { QuestionnaireAnswer } from "types/questionnaire_answer";
interface ResolutionProps {
  question: Question;
  questionnaireAnswer?: QuestionnaireAnswer;
}

export default function Resolution(props: ResolutionProps): JSX.Element {
  const { questionnaireAnswer } = props;
  const question = props.question;
  const [showChat, setShowChat] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const chatRef = useRef(null);
  const backdropRef = useRef(null);
  const router = useRouter();

  const [questionAidRating, setQuestionAidRating] = useState(
    questionnaireAnswer?.questionAidRating
  );
  const thumbsUp = questionAidRating === true;
  const thumbsDown = questionAidRating === false;

  const { user } = useUser();

  useEffect(() => {
    setIsMobileView(isMobile());
    const handleResize = () => setIsMobileView(isMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!document.querySelector("#questionnaire")) return;

    if (showChat) {
      document.querySelector("#questionnaire").classList.add("with-chat");
    } else {
      document.querySelector("#questionnaire").classList.remove("with-chat");
    }
  }, [showChat]);

  useEffect(() => {
    if (!user || questionnaireAnswer || questionAidRating !== undefined) return;

    fetchQuestionAidRating();
  }, [user, question.id]);

  // Check if chat=true query parameter is present and open chat automatically
  useEffect(() => {
    if (router.query.chat === "true") {
      setShowChat(true);
    }
  }, [router.query.chat]);

  // Function to update URL with chat query parameter
  const updateChatQueryParam = (isChatOpen: boolean) => {
    const newQuery = { ...router.query };

    if (isChatOpen) {
      newQuery.chat = "true";
    } else {
      delete newQuery.chat;
    }

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery
      },
      undefined,
      { shallow: true }
    );
  };

  // Function to close the chat and update URL
  const closeChat = () => {
    setShowChat(false);
    updateChatQueryParam(false);
  };

  async function fetchQuestionAidRating() {
    try {
      const response = await questionAidRatingsApi.getByQuestionId(question.id);
      if (response.body.data && response.body.data.value !== undefined) {
        setQuestionAidRating(response.body.data.value);
      }
    } catch (error) {
      if (error.statusCode === 404) return;

      throw error;
    }
  }

  if (!question.resolution) return <></>;

  function handleRating(value: boolean) {
    if (value == questionAidRating) return;

    updateRating(value);
  }

  function updateRating(value: boolean) {
    const oldRating = questionAidRating;

    setQuestionAidRating(value);
    questionAidRatingsApi
      .update({ questionId: question.id, value: value })
      .catch((error) => {
        setQuestionAidRating(oldRating);
        throw error;
      });
  }

  function toggleChat() {
    const newChatState = !showChat;
    setShowChat(newChatState);
    updateChatQueryParam(newChatState);
  }

  return (
    <>
      <div
        className="resolution my-6 p-4 bg-white border border-secondary-200 rounded-lg"
        key={`question-resolution-${question.id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="m-0">Resolução</h3>
          <div className="flex items-center">
            {user && (
              <div className="flex">
                <div
                  onClick={() => handleRating(true)}
                  className="thumbs-up flex items-center mr-3 cursor-pointer"
                >
                  <FiThumbsUp
                    size={20}
                    className={clsx("mr-1 text-emphasis-low", {
                      "text-success-dark": thumbsUp
                    })}
                  />
                </div>
                <div
                  onClick={() => handleRating(false)}
                  className="thumbs-down flex items-center cursor-pointer"
                >
                  <FiThumbsDown
                    size={20}
                    className={clsx("mr-1 text-emphasis-low", {
                      "text-error-dark": thumbsDown
                    })}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div dangerouslySetInnerHTML={{ __html: question.resolution }}></div>

        <div className="my-5">
          <ChatButton questionId={question.id} onClick={toggleChat} />
        </div>

        <span className="block mt-4 text-xs italic">
          * Explicação gerada com inteligência artificial. Pode conter erros.
        </span>
      </div>

      {/* Backdrop for mobile */}
      <CSSTransition
        in={showChat && isMobileView}
        timeout={300}
        classNames="fade"
        unmountOnExit
        nodeRef={backdropRef}
      >
        <div
          ref={backdropRef}
          className="fixed z-40 inset-0 bg-black bg-opacity-30"
          onClick={closeChat}
        ></div>
      </CSSTransition>

      <CSSTransition
        in={showChat}
        timeout={300}
        classNames={isMobileView ? "show-up" : "show-right-full"}
        unmountOnExit
        nodeRef={chatRef}
      >
        <div
          ref={chatRef}
          className={clsx(isMobileView ? "chat-mobile" : "chat-desktop")}
        >
          <ChatInterface
            questionId={question.id}
            onClose={closeChat}
            isMobile={isMobileView}
          />
        </div>
      </CSSTransition>

      <style jsx global>{`
        .resolution ul {
          @apply pl-4;
          list-style: square;
        }

        .resolution li {
          @apply my-2;
        }

        /* Chat transitions */
        .chat-desktop {
          position: fixed;
          top: 0;
          right: 0;
          height: 100%;
          width: 400px;
          z-index: 30;
        }

        .chat-mobile {
          position: fixed;
          inset: 0;
          z-index: 50;
        }
      `}</style>
    </>
  );
}
